import { useInfiniteQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useProfitPoolOverviewContext } from '../context/ProfitPoolOverviewContext';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT } from '../apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import useProfitPoolFiltersLoading from './useProfitPoolFiltersLoading';
import {
    buildMarginPercentageChartData,
    isMarginHierarchyQueryEnabled,
} from './marginPercentageChartData.helpers';

const useMarginPercentagebyHierarchy = () => {
    const [marginPercentageData, setMarginPercentageData] = useState<any[]>([]);
    const [minMarginPercent, setMinMarginPercent] = useState<number>(0);
    const [maxMarginPercent, setMaxMarginPercent] = useState<number>(0);
    const [minVolumeSalesEquivalent, setMinVolumeSalesEquivalent] = useState<number>(0);
    const [maxVolumeSalesEquivalent, setMaxVolumeSalesEquivalent] = useState<number>(0);
    const [groupedChartData, setGroupedChartData] = useState<any[]>([]);
    const { selectedSubFilter } = useProfitPoolOverviewContext();
    const {
        multiSelectedTiers,
        profitPoolTierList,
        idForRestOfComponent,
        allDimAndAttributeTypeList,
        timePeriodLoading,
        timePeriodSelection,
        idForRestOfComponentLoading,
    } = useContext(FilterContextData);

    const isFiltersLoading = useProfitPoolFiltersLoading();
    const isQueryEnabled = isMarginHierarchyQueryEnabled(
        allDimAndAttributeTypeList,
        timePeriodLoading,
        idForRestOfComponentLoading,
        timePeriodSelection,
    );

    const {
        data: volumeEquivalentData,
        isPending: isPendingVolumeEquivalent,
        isError: isErrorVolumeEquivalent,
        isSuccess: isSuccessVolumeEquivalent,
        fetchNextPage: fetchNextPageVolumeEquivalent,
        hasNextPage: hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPageVolumeEquivalent,
        isFetching: isFetchingVolumeEquivalent,
    } = useInfiniteQuery({
        queryKey: [
            'volume-equivalent-by-hierarchy-infi',
            selectedSubFilter?.dataKey,
            multiSelectedTiers.join(','),
            idForRestOfComponent,
            timePeriodSelection,
            'desc',
            'volume_sales_equivalent',
        ],
        queryFn: ({ pageParam, signal }) =>
            fetchProfitPoolData({
                selectedSubFilter,
                offset: pageParam as number,
                limit: PROFIT_POOL_API_LIMIT,
                kpi: 'volume_sales_equivalent',
                tierList: multiSelectedTiers,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: idForRestOfComponent,
                frequency: 'M',
                timePeriod: JSON.parse(timePeriodSelection),
                absValueSortOrder: 'desc',
                absValueSortKpi: 'volume_sales_equivalent',
                signal,
            }),
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + PROFIT_POOL_API_LIMIT,
        initialPageParam: 0,
        enabled: isQueryEnabled,
    });

    const { data, isPending, isError, isSuccess, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
        useInfiniteQuery({
            queryKey: [
                'margin-percentage-by-hierarchy-infi',
                selectedSubFilter?.dataKey,
                multiSelectedTiers.join(','),
                idForRestOfComponent,
                timePeriodSelection,
                'desc',
                'volume_sales_equivalent',
            ],
            queryFn: ({ pageParam, signal }) =>
                fetchProfitPoolData({
                    selectedSubFilter,
                    offset: pageParam as number,
                    limit: PROFIT_POOL_API_LIMIT,
                    kpi: 'margin_percent',
                    tierList: multiSelectedTiers,
                    yearAgo: false,
                    groupedByList: ['attribute'],
                    dimIdsFilter: idForRestOfComponent,
                    frequency: 'M',
                    timePeriod: JSON.parse(timePeriodSelection),
                    absValueSortOrder: 'desc',
                    absValueSortKpi: 'volume_sales_equivalent',
                    signal,
                }),
            getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + PROFIT_POOL_API_LIMIT,
            initialPageParam: 0,
            enabled: isQueryEnabled,
        });

    const fetchCombinedNextPage = () => {
        fetchNextPage();
        fetchNextPageVolumeEquivalent();
    };

    useEffect(() => {
        if (!isPending && !isPendingVolumeEquivalent && !isFetchingNextPage && !isFetchingNextPageVolumeEquivalent) {
            if (
                !isEmpty(data?.pages) &&
                data?.pages &&
                !isEmpty(volumeEquivalentData?.pages) &&
                volumeEquivalentData?.pages
            ) {
                const allData = data.pages.flatMap((page: any) => page?.data || []);
                const allVolumeEquivalentData = volumeEquivalentData.pages.flatMap((page: any) => page?.data || []);
                const chartData = buildMarginPercentageChartData(allData, allVolumeEquivalentData, profitPoolTierList);

                setMarginPercentageData(chartData.marginPercentageData);
                setGroupedChartData(chartData.groupedChartData);
                setMinMarginPercent(chartData.minMarginPercent);
                setMaxMarginPercent(chartData.maxMarginPercent);
                setMinVolumeSalesEquivalent(chartData.minVolumeSalesEquivalent);
                setMaxVolumeSalesEquivalent(chartData.maxVolumeSalesEquivalent);
            } else {
                setMarginPercentageData([]);
                setGroupedChartData([]);
                setMinMarginPercent(0);
                setMaxMarginPercent(0);
                setMinVolumeSalesEquivalent(0);
                setMaxVolumeSalesEquivalent(0);
            }
        }
    }, [
        data,
        isPending,
        volumeEquivalentData,
        isPendingVolumeEquivalent,
        isFetchingNextPage,
        isFetchingNextPageVolumeEquivalent,
        profitPoolTierList,
    ]);

    const rawMarginData = useMemo(() => data?.pages?.flatMap((page: any) => page?.data || []) ?? [], [data]);
    const rawVolumeData = useMemo(
        () => volumeEquivalentData?.pages?.flatMap((page: any) => page?.data || []) ?? [],
        [volumeEquivalentData],
    );
    const isDataSyncing =
        isSuccess &&
        isSuccessVolumeEquivalent &&
        rawMarginData.length > 0 &&
        rawVolumeData.length > 0 &&
        marginPercentageData.length === 0;
    const isLoading =
        isPending ||
        isPendingVolumeEquivalent ||
        isFetching ||
        isFetchingVolumeEquivalent ||
        isFiltersLoading ||
        isDataSyncing;

    return {
        data: marginPercentageData,
        groupedChartData,
        isPending: isPending || isPendingVolumeEquivalent,
        isLoading,
        isError: isError || isErrorVolumeEquivalent,
        isSuccess: isSuccess || isSuccessVolumeEquivalent,
        fetchNextPage: fetchCombinedNextPage,
        hasNextPage: hasNextPage || hasNextPageVolumeEquivalent,
        isFetchingNextPage: isFetchingNextPage || isFetchingNextPageVolumeEquivalent,
        minMarginPercent,
        maxMarginPercent,
        minVolumeSalesEquivalent,
        maxVolumeSalesEquivalent,
    };
};

export default useMarginPercentagebyHierarchy;
